import { calculateCompoundInterest } from "@/lib/calculators/compound-interest";

export type ContributionFrequency = "monthly" | "quarterly" | "annually";
export type CompoundingFrequency =
  | "daily"
  | "monthly"
  | "quarterly"
  | "annually"
  | "continuously";

const CONTRIBUTION_FREQ_MAP: Record<ContributionFrequency, number> = {
  monthly: 12,
  quarterly: 4,
  annually: 1,
};

const COMPOUNDING_FREQ_MAP: Record<CompoundingFrequency, number> = {
  daily: 365,
  monthly: 12,
  quarterly: 4,
  annually: 1,
  continuously: 365, // approximate with daily (effectively same)
};

export type SolveMode = "futureValue" | "monthlyContribution" | "years";

export interface InvestmentInput {
  initialInvestment: number;
  monthlyContribution: number;
  contributionFrequency: ContributionFrequency;
  expectedReturn: number;
  years: number;
  compoundingFrequency: CompoundingFrequency;
  adjustForInflation?: boolean;
  inflationRate?: number;
  /** Tax rate on investment gains (0-50%), for taxable accounts */
  taxRate?: number;
  solveMode?: SolveMode;
  /** Target future value when solving for contribution or years */
  targetFutureValue?: number;
}

export interface YearlyBreakdownRow {
  year: number;
  balance: number;
  contribution: number;
  interest: number;
}

export interface InvestmentResult {
  futureValue: number;
  totalContributions: number;
  totalInterest: number;
  principal: number;
  chartData: { year: number; balance: number }[];
  principalVsInterest: { principal: number; interest: number };
  realFutureValue?: number;
  realPurchasingPower?: number;
  /** Solved monthly contribution (when solveMode is "monthlyContribution") */
  solvedMonthlyContribution?: number;
  /** Solved years to goal (when solveMode is "years") */
  solvedYears?: number;
  /** Year-by-year breakdown for amortization table */
  yearlyBreakdown: YearlyBreakdownRow[];
  /** After-tax future value (when taxRate > 0) */
  afterTaxFutureValue?: number;
}

/** Convert investment input to compound-interest input */
function toCompoundInput(
  input: Omit<InvestmentInput, "adjustForInflation" | "inflationRate">
): Parameters<typeof calculateCompoundInterest>[0] {
  const contribFreq = CONTRIBUTION_FREQ_MAP[input.contributionFrequency];
  const compoundFreq = COMPOUNDING_FREQ_MAP[input.compoundingFrequency];

  // Contribution amount per period: monthly contribution * (12 / contribFreq)
  // e.g. $500/month with quarterly = $1500 per quarter
  const contributionPerPeriod =
    input.monthlyContribution * (12 / contribFreq);

  return {
    principal: input.initialInvestment,
    rate: input.expectedReturn,
    years: input.years,
    frequency: compoundFreq,
    contribution: contributionPerPeriod,
    contributionFrequency: contribFreq,
  };
}

/** Binary search for monthly contribution to reach target FV */
function solveForMonthlyContribution(
  input: Omit<InvestmentInput, "monthlyContribution" | "adjustForInflation" | "inflationRate">
): number {
  const target = input.targetFutureValue ?? 0;
  if (target <= input.initialInvestment) return 0;

  let low = 0;
  let high = 1_000_000; // max monthly contribution to search
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const testInput = { ...input, monthlyContribution: mid };
    const ci = calculateCompoundInterest(toCompoundInput(testInput));
    if (ci.total >= target) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return Math.round(((low + high) / 2) * 100) / 100;
}

/** Binary search for years to reach target FV */
function solveForYears(
  input: Omit<InvestmentInput, "years" | "adjustForInflation" | "inflationRate">
): number {
  const target = input.targetFutureValue ?? 0;
  const ciAt50 = calculateCompoundInterest(
    toCompoundInput({ ...input, years: 50 })
  );
  if (ciAt50.total < target) return 50; // cap at 50 years

  let low = 0;
  let high = 50;
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const testInput = { ...input, years: mid };
    const ci = calculateCompoundInterest(toCompoundInput(testInput));
    if (ci.total >= target) {
      high = mid;
    } else {
      low = mid;
    }
  }
  return Math.round(((low + high) / 2) * 10) / 10;
}

export function calculateInvestment(input: InvestmentInput): InvestmentResult {
  const {
    adjustForInflation = false,
    inflationRate = 2.5,
    taxRate = 0,
    solveMode = "futureValue",
    targetFutureValue = 0,
  } = input;

  let effectiveContribution = input.monthlyContribution;
  let effectiveYears = input.years;

  if (solveMode === "monthlyContribution" && targetFutureValue > 0) {
    effectiveContribution = solveForMonthlyContribution({
      ...input,
      targetFutureValue,
    });
  } else if (solveMode === "years" && targetFutureValue > 0) {
    effectiveYears = solveForYears({ ...input, targetFutureValue });
  }

  const compoundInput = toCompoundInput({
    ...input,
    monthlyContribution: effectiveContribution,
    years: effectiveYears,
  });
  const ci = calculateCompoundInterest(compoundInput);
  const contribFreq = CONTRIBUTION_FREQ_MAP[input.contributionFrequency];
  const contributionPerYear =
    effectiveContribution * 12; /* monthly equiv * 12 months */

  const yearlyBreakdown: YearlyBreakdownRow[] = [];
  for (let y = 0; y < ci.chartData.length; y++) {
    const curr = ci.chartData[y];
    const prev = ci.chartData[y - 1];
    const contribThisYear = y === 0 ? 0 : contributionPerYear;
    const interestThisYear =
      y === 0 ? 0 : Math.round((curr.total - (prev?.total ?? 0) - contribThisYear) * 100) / 100;
    yearlyBreakdown.push({
      year: curr.year,
      balance: Math.round(curr.total * 100) / 100,
      contribution: contribThisYear,
      interest: interestThisYear,
    });
  }

  const result: InvestmentResult = {
    futureValue: ci.total,
    totalContributions: ci.totalContributions ?? 0,
    totalInterest: ci.interest,
    principal: ci.principal,
    chartData: ci.chartData.map((d) => ({ year: d.year, balance: d.total })),
    principalVsInterest: {
      principal: ci.principal + (ci.totalContributions ?? 0),
      interest: ci.interest,
    },
    yearlyBreakdown,
    ...(solveMode === "monthlyContribution" && {
      solvedMonthlyContribution: effectiveContribution,
    }),
    ...(solveMode === "years" && { solvedYears: effectiveYears }),
  };

  if (adjustForInflation && inflationRate > 0) {
    const inflationFactor = Math.pow(1 + inflationRate / 100, input.years);
    result.realFutureValue = Math.round(
      (result.futureValue / inflationFactor) * 100
    ) / 100;
    result.realPurchasingPower = result.realFutureValue;
  }

  if (taxRate > 0 && taxRate <= 100) {
    const taxOnGains = result.totalInterest * (taxRate / 100);
    result.afterTaxFutureValue =
      Math.round((result.futureValue - taxOnGains) * 100) / 100;
  }

  return result;
}

/** Scenario rates: Low 4%, Medium 7%, High 10% */
export const SCENARIO_RATES = { low: 4, medium: 7, high: 10 } as const;

export interface InvestmentScenariosResult {
  low: InvestmentResult;
  medium: InvestmentResult;
  high: InvestmentResult;
}

export function calculateInvestmentScenarios(
  input: Omit<InvestmentInput, "expectedReturn"> & {
    solveMode?: SolveMode;
    targetFutureValue?: number;
  }
): InvestmentScenariosResult {
  return {
    low: calculateInvestment({ ...input, expectedReturn: SCENARIO_RATES.low }),
    medium: calculateInvestment({
      ...input,
      expectedReturn: SCENARIO_RATES.medium,
    }),
    high: calculateInvestment({ ...input, expectedReturn: SCENARIO_RATES.high }),
  };
}
